import { Head, router, usePage } from "@inertiajs/react"
import { ArrowRight } from "@phosphor-icons/react"
import { useState } from "react"

import { AgentAvatar } from "@/components/agents/agent-icon"
import { PageHeading, PageLayout } from "@/components/page-layout"
import { Button } from "@/components/ui/button"
import { Empty } from "@/components/ui/empty"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import { activationAgentsPath, agentPath, agentsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [{ title: "Agents", href: agentsPath() }]

type Outcome = "increase-sales" | "boost-productivity" | "improve-retention"

interface OutcomeMeta {
  id: Outcome
  label: string
  dotClass: string
  iconGradient: string
  pillActiveClass: string
}

const OUTCOME_META: Record<Outcome, OutcomeMeta> = {
  "increase-sales": {
    id: "increase-sales",
    label: "Increase Sales",
    dotClass: "bg-blue-500",
    iconGradient: "from-blue-400 to-blue-600",
    pillActiveClass: "border-blue-500 bg-blue-50 text-blue-800",
  },
  "boost-productivity": {
    id: "boost-productivity",
    label: "Boost Productivity",
    dotClass: "bg-purple-500",
    iconGradient: "from-purple-400 to-purple-600",
    pillActiveClass: "border-purple-500 bg-purple-50 text-purple-800",
  },
  "improve-retention": {
    id: "improve-retention",
    label: "Improve Retention",
    dotClass: "bg-yellow-400",
    iconGradient: "from-yellow-400 to-amber-500",
    pillActiveClass: "border-yellow-400 bg-yellow-50 text-yellow-800",
  },
}

const BENEFIT_BADGE_COLORS = [
  "bg-pink-100 text-pink-700",
  "bg-teal-100 text-teal-700",
  "bg-orange-100 text-orange-700",
  "bg-indigo-100 text-indigo-700",
]

const OUTCOME_BENEFIT_CLASS: Record<Outcome, string> = {
  "increase-sales": "bg-blue-100 text-blue-700",
  "boost-productivity": "bg-purple-100 text-purple-700",
  "improve-retention": "bg-amber-100 text-amber-700",
}

function benefitBadgeClass(outcome: Outcome | null, text: string): string {
  if (outcome && OUTCOME_BENEFIT_CLASS[outcome]) {
    return OUTCOME_BENEFIT_CLASS[outcome]
  }
  let hash = 0
  for (let i = 0; i < text.length; i++)
    hash = (hash * 31 + text.charCodeAt(i)) | 0
  return BENEFIT_BADGE_COLORS[Math.abs(hash) % BENEFIT_BADGE_COLORS.length]
}

interface CatalogAgent {
  uid: string
  name: string
  description: string | null
  benefit: string | null
  icon: string | null
  outcome: Outcome | null
  integrations: string[]
  retail_price_cents: number
  active: boolean
  agent_uid: string | null
}

interface Props {
  catalog_agents: CatalogAgent[]
  outcomes: Outcome[]
}

function fmt(cents: number) {
  return (
    "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0 })
  )
}

export default function AgentsIndex() {
  const { catalog_agents, outcomes } = usePage().props as unknown as Props
  const [selected, setSelected] = useState<Outcome | "all">("all")
  const [sort, setSort] = useState("recommended")

  const filtered =
    selected === "all"
      ? catalog_agents
      : catalog_agents.filter((a) => a.outcome === selected)

  const visible = [...filtered].sort((a, b) => {
    if (sort === "price-asc") return a.retail_price_cents - b.retail_price_cents
    if (sort === "price-desc")
      return b.retail_price_cents - a.retail_price_cents
    return 0
  })

  function handlePrimaryAction(agent: CatalogAgent) {
    if (agent.active && agent.agent_uid) {
      router.visit(agentPath({ id: agent.agent_uid }))
    } else {
      router.visit(activationAgentsPath(agent.uid))
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Agents" />

      <PageLayout>
        <div className="flex flex-col gap-4">
          <div>
            <PageHeading>Agents</PageHeading>
            <p className="text-muted-foreground mt-1 text-sm">
              Plug-and-play AI agents for your business. Pick one to get
              started.
            </p>
          </div>

          <div className="mt-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelected("all")}
                className={cn(
                  "border-border bg-background hover:bg-muted rounded-2xl border px-3 py-1.5 transition-colors",
                  selected === "all" &&
                    "border-border bg-muted text-foreground",
                )}
              >
                <div className="flex items-center">
                  <span className="text-sm font-medium">All</span>
                </div>
              </button>
              {outcomes.map((id) => {
                const meta = OUTCOME_META[id]
                if (!meta) return null
                const active = selected === id
                return (
                  <button
                    key={id}
                    onClick={() => setSelected(id)}
                    className={cn(
                      "border-border bg-background hover:bg-muted rounded-2xl border px-3 py-1.5 text-left transition-colors",
                      active && meta.pillActiveClass,
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "size-2 shrink-0 rounded-full",
                          meta.dotClass,
                        )}
                      />
                      <span className="text-sm font-medium">{meta.label}</span>
                    </div>
                  </button>
                )
              })}
            </div>

            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="recommended">Recommended</SelectItem>
                <SelectItem value="price-asc">Price: Low to High</SelectItem>
                <SelectItem value="price-desc">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {visible.length === 0 ? (
            <div className="bg-card rounded-2xl border">
              <Empty title="No agents available yet." />
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((agent) => {
                return (
                  <div
                    key={agent.uid}
                    className="bg-card flex flex-col overflow-hidden rounded-2xl border shadow-xs"
                  >
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <AgentAvatar
                          icon={agent.icon}
                          outcome={agent.outcome}
                        />
                        {agent.benefit && (
                          <span
                            className={cn(
                              "rounded-md px-2 py-1 text-xs font-semibold",
                              benefitBadgeClass(agent.outcome, agent.benefit),
                            )}
                          >
                            {agent.benefit}
                          </span>
                        )}
                      </div>

                      <p className="mt-4 text-base font-medium">{agent.name}</p>
                      {agent.description && (
                        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                          {agent.description}
                        </p>
                      )}
                    </div>

                    <div className="bg-muted/30 flex items-center justify-between gap-2 border-t px-4 py-2.5">
                      {agent.active ? (
                        <span className="text-muted-foreground shrink-0 text-sm">
                          Subscribed
                        </span>
                      ) : (
                        <span className="shrink-0 text-sm font-medium">
                          <span className="text-foreground">
                            {fmt(agent.retail_price_cents)}
                          </span>
                          <span className="text-muted-foreground">/mo</span>
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePrimaryAction(agent)}
                      >
                        {agent.active ? (
                          "Manage"
                        ) : (
                          <>
                            Get started <ArrowRight className="size-3.5" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </PageLayout>
    </AppLayout>
  )
}
